/**
 * The one-line status a subscription shows under its name on the account page —
 * "Active · renews automatically", "Cancelling", "Past due".
 *
 * Kept out of the directive so the wording rules are unit-testable without a scope.
 * The template only binds `label` and switches a dot colour on `tone`; it decides
 * nothing itself, which is what the previous markup got wrong — the same four
 * conditions were repeated across seven `ng-show` attributes and drifted apart.
 */

export type SubscriptionStatusTone = 'active' | 'attention' | 'muted';

export interface SubscriptionStatusView {
    label: string;
    tone: SubscriptionStatusTone;
}

/** Only the fields the status line reads. Keeps the signature honest for callers and specs. */
export interface SubscriptionStatusInput {
    status?: string | null;
    isCancelling?: boolean | null;
    autoCollect?: boolean | null;
}

/** Whether a subscription's next billing date is actually its end date. */
export function isSubscriptionWindingDown(subscription: SubscriptionStatusInput): boolean {
    if (subscription.isCancelling) return true;

    const status = (subscription.status || '').toLowerCase();
    return status !== 'active' && status !== 'trialing';
}

/**
 * Stripe statuses that mean "this subscription needs the user to do something".
 * Anything unrecognised is shown verbatim rather than guessed at — a status we have
 * not seen before is better displayed than silently relabelled "Active".
 */
const ATTENTION_STATUSES = ['past_due', 'unpaid', 'incomplete', 'incomplete_expired', 'paused'];

export function describeSubscriptionStatus(subscription: SubscriptionStatusInput | null | undefined): SubscriptionStatusView {
    if (!subscription) return { label: '', tone: 'muted' };

    // Cancelling outranks the raw status: Stripe still reports `active` for a
    // subscription that is set to cancel at period end, and "Active" is the one thing
    // that user must not be told.
    if (subscription.isCancelling) return { label: 'Cancelling', tone: 'attention' };

    const status = (subscription.status || '').toLowerCase();

    if (status === 'active') {
        return subscription.autoCollect
            ? { label: 'Active · renews automatically', tone: 'active' }
            : { label: 'Active', tone: 'active' };
    }

    if (status === 'trialing') return { label: 'Trial', tone: 'active' };
    if (status === 'canceled' || status === 'cancelled') return { label: 'Cancelled', tone: 'muted' };
    if (!status) return { label: '', tone: 'muted' };

    return {
        label: humanise(status),
        tone: ATTENTION_STATUSES.indexOf(status) >= 0 ? 'attention' : 'muted',
    };
}

/** `past_due` → `Past due`. Stripe statuses are snake_case; users are not. */
function humanise(status: string): string {
    const spaced = status.replace(/_/g, ' ');
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * How many subscriptions to report as active beside the card title. Counts what the
 * user still holds — a subscription cancelling at period end has not lapsed yet — so
 * the number matches the rows on screen rather than Stripe's billing state.
 */
export function countActiveSubscriptions(subscriptions: SubscriptionStatusInput[] | null | undefined): number {
    if (!subscriptions) return 0;
    return subscriptions.filter(subscription => {
        const status = (subscription.status || '').toLowerCase();
        return status === 'active' || status === 'trialing';
    }).length;
}
